// src/pages/students/details/PaymentsTab.jsx
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Card from '../../../components/common/Card';
import Button from '../../../components/common/Button';
import { usePayments } from '../../../hooks/usePayments';
import { CreditCardIcon } from '@heroicons/react/24/outline';

export default function PaymentsTab({ studentId }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    payments = [],
    loading: paymentsLoading,
    fetching: paymentsFetching,
    error: paymentsError,
    softDeletePayment,
  } = usePayments();

  const [deletingPaymentId, setDeletingPaymentId] = useState(null);

  const studentPayments = useMemo(
    () => (payments || []).filter((p) => String(p.student_id) === String(studentId)),
    [payments, studentId]
  );

  const paymentStatusBadge = (status) => {
    switch (status) {
      case 'Paid':
        return 'bg-green-100 text-green-800';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'Refunded':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const paymentTypeBadge = (type) =>
    type === 'Full' ? 'bg-purple-100 text-purple-800' : 'bg-indigo-100 text-indigo-800';

  const handleRemovePayment = async (payment) => {
    const ok = window.confirm(
      'Remove this payment? This is a soft delete and can be restored from the database if needed.'
    );
    if (!ok) return;

    setDeletingPaymentId(payment.id);
    try {
      await softDeletePayment({ id: payment.id, student_id: payment.student_id });
      // keep Enrollments "Paid" pills in sync if user switches tabs
      queryClient.invalidateQueries({ queryKey: ['paid_enrollment_ids', studentId] });
    } catch (err) {
      console.error(err);
      alert('Failed to remove payment.');
    } finally {
      setDeletingPaymentId(null);
    }
  };

  return (
    <Card>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-medium text-gray-900">Payments</h2>
            {paymentsFetching && studentPayments.length > 0 && (
              <span className="text-xs text-gray-400">Refreshing…</span>
            )}
          </div>
          <Button onClick={() => navigate(`/students/${studentId}/payments/new`)} className="flex items-center">
            <CreditCardIcon className="h-4 w-4 mr-1" />
            Add Payment
          </Button>
        </div>

        {paymentsLoading ? (
          <div className="py-8 text-center text-gray-500">Loading payments…</div>
        ) : paymentsError ? (
          <div className="py-8 text-center text-red-600">Error: {paymentsError}</div>
        ) : studentPayments.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            No payments yet{' '}
            <Link to={`/students/${studentId}/payments/new`} className="text-blue-600 hover:text-blue-800 underline">
              Add Payment
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Method
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {studentPayments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{payment.invoice_number}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        RM{Number(payment.amount).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {payment.payment_date
                          ? new Date(payment.payment_date).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            })
                          : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{payment.payment_method}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${paymentTypeBadge(
                          payment.payment_type
                        )}`}
                      >
                        {payment.payment_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${paymentStatusBadge(
                          payment.status
                        )}`}
                      >
                        {payment.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <Link to={`/payments/${payment.id}?fromStudent=${studentId}`} className="text-blue-600 hover:text-blue-900">
                          View
                        </Link>
                        <Link
                          to={`/payments/${payment.id}/edit?fromStudent=${studentId}`}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          Edit
                        </Link>
                        <button
                          onClick={() => handleRemovePayment(payment)}
                          disabled={deletingPaymentId === payment.id}
                          className="text-red-600 hover:text-red-800 disabled:opacity-50"
                          title="Remove payment"
                        >
                          {deletingPaymentId === payment.id ? 'Removing…' : 'Remove'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}
